import React, { useCallback, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { GoogleGIcon } from '../../components/auth/GoogleGIcon';
import Input from '../../components/ui/Input';
import { getQueryParam } from '../../shared/queryParams';
import type { RegisterForm } from './types';
import {
  ACTION_COL_CLASS,
  labelClassAuth,
  outlineCtaClassName,
  primaryCtaClassName,
  REGISTER_CARD_CLASS,
  RegisterBackButton,
  RegisterStepFooter,
} from './registerShared';

const EMAIL_RE = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;
const NIF_RE = /^(?:\d{8}[A-Z]|[XYZ]\d{7}[A-Z])$/;
const PW_RE = /^(?=.*[A-Z])(?=.*\d).{8,}$/;

type RegisterStepCreateAccountProps = {
  data: RegisterForm;
  onChange: (patch: Partial<RegisterForm>) => void;
  onBack: () => void;
  onNext: () => void;
  error?: string | null;
  submitting?: boolean;
};

const RegisterStepCreateAccount: React.FC<RegisterStepCreateAccountProps> = ({
  data,
  onChange,
  onBack,
  onNext,
  error,
  submitting,
}) => {
  const { t } = useTranslation(['auth', 'common']);
  const location = useLocation();
  const [touched, setTouched] = useState({ email: false, nif: false, password: false });

  const emailError = useCallback(
    (v: string) => {
      if (!v.trim()) return null;
      return EMAIL_RE.test(v.trim()) ? null : t('auth:validation.email_invalid');
    },
    [t],
  );

  const nifError = useCallback(
    (v: string) => {
      if (!v.trim()) return null;
      return NIF_RE.test(v.trim().toUpperCase()) ? null : t('auth:validation.nif_invalid');
    },
    [t],
  );

  const passwordError = useCallback(
    (v: string) => {
      if (!v) return null;
      return PW_RE.test(v) ? null : t('auth:validation.password_rules');
    },
    [t],
  );

  useEffect(() => {
    const emailParam = getQueryParam(location.search, 'email');
    if (emailParam && !data.email) {
      onChange({ email: emailParam });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const touch = (field: keyof typeof touched) =>
    setTouched((prev) => ({ ...prev, [field]: true }));

  const emailErr = touched.email ? emailError(data.email) : null;
  const nifErr = touched.nif ? nifError(data.nif) : null;
  const pwErr = touched.password ? passwordError(data.password) : null;
  const pwMismatch =
    data.password && data.confirmPassword && data.password !== data.confirmPassword;

  const hasFieldErrors = !!(
    emailError(data.email) ||
    nifError(data.nif) ||
    passwordError(data.password)
  );

  const onSubmit = useCallback(
    (e: React.FormEvent) => {
      e.preventDefault();
      setTouched({ email: true, nif: true, password: true });
      if (!data.agreeTerms) return;
      if (data.password !== data.confirmPassword) return;
      if (hasFieldErrors) return;
      onNext();
    },
    [data, hasFieldErrors, onNext],
  );

  return (
    <form className={REGISTER_CARD_CLASS} onSubmit={onSubmit} noValidate>
      <div>
        <RegisterBackButton onBack={onBack} />
        <h1 className="text-center text-[22px] font-semibold leading-tight text-gray-900">
          {t('auth:register.create.title')}
        </h1>
      </div>

      <div className="flex flex-col gap-4">
        <Input
          id="reg-name"
          name="name"
          autoComplete="name"
          label={t('auth:register.create.name_label')}
          labelClassName={labelClassAuth}
          placeholder={t('auth:register.create.name_placeholder')}
          value={data.name}
          onChange={(e) => onChange({ name: e.target.value })}
          className="!focus:border-[#6ECFCF] !focus:ring-[#6ECFCF]"
        />
        <div className="flex flex-col gap-1">
          <Input
            id="reg-email"
            name="email"
            type="email"
            autoComplete="email"
            label={t('auth:register.create.email_label')}
            labelClassName={labelClassAuth}
            placeholder={t('auth:shared.email_placeholder')}
            value={data.email}
            onChange={(e) => onChange({ email: e.target.value })}
            onBlur={() => touch('email')}
            className="!focus:border-[#6ECFCF] !focus:ring-[#6ECFCF]"
          />
          {emailErr ? (
            <p className="text-sm text-red-600" role="alert">
              {emailErr}
            </p>
          ) : null}
        </div>
        <div className="flex flex-col gap-1">
          <Input
            id="reg-nif"
            name="nif"
            autoComplete="off"
            label={t('auth:register.create.nif_label')}
            labelClassName={labelClassAuth}
            placeholder={t('auth:register.create.nif_placeholder')}
            value={data.nif}
            onChange={(e) => onChange({ nif: e.target.value.toUpperCase() })}
            onBlur={() => touch('nif')}
            className="!focus:border-[#6ECFCF] !focus:ring-[#6ECFCF]"
          />
          {nifErr ? (
            <p className="text-sm text-red-600" role="alert">
              {nifErr}
            </p>
          ) : null}
        </div>
        <div className="flex flex-col gap-1">
          <Input
            id="reg-password"
            name="password"
            type="password"
            autoComplete="new-password"
            label={t('auth:register.create.password_label')}
            labelClassName={labelClassAuth}
            placeholder={t('auth:shared.password_placeholder')}
            value={data.password}
            onChange={(e) => onChange({ password: e.target.value })}
            onBlur={() => touch('password')}
            className="!focus:border-[#6ECFCF] !focus:ring-[#6ECFCF]"
          />
          {pwErr ? (
            <p className="text-sm text-red-600" role="alert">
              {pwErr}
            </p>
          ) : (
            <p className="text-xs text-gray-500">{t('auth:register.create.password_hint')}</p>
          )}
        </div>
        <Input
          id="reg-confirm"
          name="confirmPassword"
          type="password"
          autoComplete="new-password"
          label={t('auth:register.create.confirm_label')}
          labelClassName={labelClassAuth}
          placeholder={t('auth:register.create.repeat_placeholder')}
          value={data.confirmPassword}
          onChange={(e) => onChange({ confirmPassword: e.target.value })}
          className="!focus:border-[#6ECFCF] !focus:ring-[#6ECFCF]"
        />
        {pwMismatch ? (
          <p className="text-sm text-red-600" role="alert">
            {t('auth:register.create.password_mismatch')}
          </p>
        ) : null}
        {error ? (
          <p className="text-sm text-red-600" role="alert">
            {error}
          </p>
        ) : null}
      </div>

      <div className="flex flex-col gap-3">
        <div className="flex items-start gap-3">
          <input
            id="reg-agree"
            type="checkbox"
            className="mt-0.5 h-4 w-4 shrink-0 rounded border-gray-300 text-[#6ECFCF] focus:ring-[#6ECFCF] accent-[#6ECFCF]"
            checked={data.agreeTerms}
            onChange={(e) => onChange({ agreeTerms: e.target.checked })}
          />
          <label htmlFor="reg-agree" className="text-sm text-gray-800">
            {t('auth:register.create.terms_prefix')}{' '}
            <a
              href="#terms"
              className="font-semibold text-gray-900 underline"
              onClick={(e) => e.preventDefault()}
            >
              {t('auth:register.create.terms_link')}
            </a>{' '}
            {t('auth:register.create.terms_and')}{' '}
            <a
              href="#privacy"
              className="font-semibold text-gray-900 underline"
              onClick={(e) => e.preventDefault()}
            >
              {t('auth:register.create.privacy_link')}
            </a>
            .
          </label>
        </div>
        <div className="flex items-start gap-3">
          <input
            id="reg-marketing"
            type="checkbox"
            className="mt-0.5 h-4 w-4 shrink-0 rounded border-gray-300 text-[#6ECFCF] focus:ring-[#6ECFCF] accent-[#6ECFCF]"
            checked={data.marketing}
            onChange={(e) => onChange({ marketing: e.target.checked })}
          />
          <label htmlFor="reg-marketing" className="text-sm text-gray-800">
            {t('auth:register.create.marketing')}
          </label>
        </div>
      </div>

      <div className={ACTION_COL_CLASS}>
        <button
          type="submit"
          className={primaryCtaClassName}
          disabled={
            submitting ||
            !data.name.trim() ||
            !data.email.trim() ||
            !data.nif.trim() ||
            !data.password ||
            !data.confirmPassword ||
            !data.agreeTerms
          }
        >
          {submitting ? t('auth:register.create.creating') : t('common:buttons.continue')}
        </button>
        <div className="relative w-full">
          <div className="absolute inset-0 flex items-center" aria-hidden>
            <span className="w-full border-t border-[#E2E8F0]" />
          </div>
          <div className="relative flex justify-center text-xs tracking-wide text-gray-500">
            <span className="bg-[#F9FAFB] px-2">{t('auth:register.create.or_divider')}</span>
          </div>
        </div>
        <button type="button" className={outlineCtaClassName}>
          <GoogleGIcon />
          {t('auth:register.create.google')}
        </button>
      </div>

      <RegisterStepFooter step={2} />
    </form>
  );
};

export default RegisterStepCreateAccount;
